import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";

// Layout Components
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import DashboardLayout from "./components/layout/DashboardLayout";

// Public Pages
import HomePage from "./pages/HomePage";
import TrackPage from "./pages/TrackPage";
import VerifyPage from "./pages/VerifyPage";
import QuotePage from "./pages/QuotePage";
import BookingPage from "./pages/BookingPage";
import SchedulesPage from "./pages/SchedulesPage";
import ServicesPage, { ServiceDetailPage } from "./pages/ServicesPage";
import ContactPage from "./pages/ContactPage";
import { IndustriesPage, OfficesPage, NewsPage, CareersPage } from "./pages/CompanyPages";
import { AboutPage, SustainabilityPage, ContainerSpecsPage, HelpPage, LegalPage } from "./pages/InfoPages";

// Auth Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// User Dashboard Pages
import UserDashboard from "./pages/dashboard/UserDashboard";
import UserBookings from "./pages/dashboard/UserBookings";
import UserQuotes from "./pages/dashboard/UserQuotes";
import UserTracking from "./pages/dashboard/UserTracking";
import UserPayments from "./pages/dashboard/UserPayments";
import UserSettings from "./pages/dashboard/UserSettings";
import UserDocuments from "./pages/dashboard/UserDocuments";
import BookingPaymentPage from "./pages/dashboard/BookingPaymentPage";

// Agent Pages
import AgentDashboard from "./pages/agent/AgentDashboard";
import AgentBookings from "./pages/agent/AgentBookings";
import AgentContainers from "./pages/agent/AgentContainers";
import AgentDocuments from "./pages/agent/AgentDocuments";
import ShippingInstructionsPage from "./pages/ShippingInstructionsPage";
import AgentApprovals from "./pages/agent/AgentApprovals";
import AgentCustomers from "./pages/agent/AgentCustomers";
import AgentWalkinBooking from "./pages/agent/AgentWalkinBooking";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPorts from "./pages/admin/AdminPorts";
import AdminFleet from "./pages/admin/AdminFleet";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminMessages from "./pages/admin/AdminMessages";
import RequireAuth from "./components/RequireAuth";
import { ScrollToTop } from "./components/shared/ScrollToTop";
import AdminAgents from "./pages/admin/AdminAgents";
import AdminPricing from "./pages/admin/AdminPricing";

// Public Layout Wrapper
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </>
);

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/track" element={<PublicLayout><TrackPage /></PublicLayout>} />
            <Route path="/verify/:sigId" element={<PublicLayout><VerifyPage /></PublicLayout>} />
            <Route path="/quote" element={<PublicLayout><QuotePage /></PublicLayout>} />
            <Route path="/booking" element={<RequireAuth><PublicLayout><BookingPage /></PublicLayout></RequireAuth>} />
            <Route path="/schedules" element={<PublicLayout><SchedulesPage /></PublicLayout>} />
            <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
            <Route path="/services/:slug" element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
            <Route path="/industries" element={<PublicLayout><IndustriesPage /></PublicLayout>} />
            <Route path="/containers" element={<PublicLayout><ContainerSpecsPage /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/sustainability" element={<PublicLayout><SustainabilityPage /></PublicLayout>} />
            <Route path="/news" element={<PublicLayout><NewsPage /></PublicLayout>} />
            <Route path="/careers" element={<PublicLayout><CareersPage /></PublicLayout>} />
            <Route path="/offices" element={<PublicLayout><OfficesPage /></PublicLayout>} />
            <Route path="/help" element={<PublicLayout><HelpPage /></PublicLayout>} />
            <Route path="/legal/:slug" element={<PublicLayout><LegalPage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />

            {/* Auth Routes (no navbar/footer) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* User Dashboard Routes */}
            <Route path="/dashboard" element={<DashboardLayout type="user" />}>
              <Route index element={<UserDashboard />} />
              <Route path="bookings" element={<UserBookings />} />
              <Route path="bookings/:bookingId/shipping-instructions" element={<ShippingInstructionsPage />} />
              <Route path="quotes" element={<UserQuotes />} />
              <Route path="tracking" element={<UserTracking />} />
              <Route path="payments" element={<UserPayments />} />
              <Route path="payments/:bookingId" element={<BookingPaymentPage />} />
              <Route path="documents" element={<UserDocuments />} />
              <Route path="settings" element={<UserSettings />} />
            </Route>

            {/* Agent Routes */}
            <Route path="/agent" element={<DashboardLayout type="agent" />}>
              <Route index element={<AgentDashboard />} />
              <Route path="bookings" element={<AgentBookings />} />
              <Route path="bookings/:bookingId/shipping-instructions" element={<ShippingInstructionsPage />} />
              <Route path="walkin-booking" element={<AgentWalkinBooking />} />
              <Route path="containers" element={<AgentContainers />} />
              <Route path="documents" element={<AgentDocuments />} />
              <Route path="approvals" element={<AgentApprovals />} />
              <Route path="customers" element={<AgentCustomers />} />
              <Route path="quotes" element={<UserQuotes />} />
              <Route path="reports" element={<AgentDashboard />} />
            </Route>

            {/* Admin Routes */}
            <Route path="/admin" element={<DashboardLayout type="admin" />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="agents" element={<AdminAgents />} />
              <Route path="bookings" element={<AgentBookings />} />
              <Route path="bookings/:bookingId/shipping-instructions" element={<ShippingInstructionsPage />} />
              <Route path="walkin-booking" element={<AgentWalkinBooking />} />
              <Route path="containers" element={<AgentContainers />} />
              <Route path="documents" element={<AgentDocuments />} />
              <Route path="approvals" element={<AgentApprovals />} />
              <Route path="fleet" element={<AdminFleet />} />
              <Route path="ports" element={<AdminPorts />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="analytics" element={<AdminDashboard />} />
              <Route path="settings" element={<UserSettings />} />
            </Route>

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
